import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionsLists, TransactionType } from 'vopay';
import AutoBalanceTransferTransactionInfo from './AutoBalanceTransferTransactionInfo';
import useFetchEFTWithdrawTransaction from '@/hooks/useApi/useFetchEFTWithdrawTransaction';
import useFetchACHWithdrawTransaction from '@/hooks/useApi/useFetchACHWithdrawTransaction';
import useFetchAutoBalanceTransferReport from '@/hooks/useApi/useFetchAutoBalanceTransferReport';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type AutoBalanceTransferTransactionContainerProps = {
  transactionID: string;
  transactionHeader?: TransactionsLists;
} & Omit<
  ComponentProps<typeof AutoBalanceTransferTransactionInfo>,
  'transaction' | 'autoBalanceReportTransactions' | 'transactionHeader'
>;

export default function AutoBalanceTransferTransactionContainer({
  transactionID,
  transactionHeader,
  jwt,
  ...props
}: AutoBalanceTransferTransactionContainerProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { eftWithdrawTransaction, fetchEftWithdrawTransaction } = useFetchEFTWithdrawTransaction({ bearer: jwt });

  const { achWithdrawTransaction, fetchAchWithdrawTransaction } = useFetchACHWithdrawTransaction({ bearer: jwt });

  const { autoBalanceReportTransactions, fetchAutoBalanceTransferReport } = useFetchAutoBalanceTransferReport({
    bearer: jwt,
  });

  const transaction =
    transactionHeader?.TransactionType === TransactionType.ACH_WITHDRAWAL
      ? achWithdrawTransaction
      : eftWithdrawTransaction;

  useEffect(() => {
    blockingLoader.show();

    // The report is a supplementary section: its hook throws on failure like every other, but here
    // the call site swallows that rejection (the Info component renders the absence as an inline
    // "could not be retrieved" note) instead of blanking the whole panel with an error dialog.
    const reportRequest = fetchAutoBalanceTransferReport(transactionID).catch(() => undefined);

    let transactionRequest: Promise<unknown> = Promise.resolve();

    if (transactionHeader?.TransactionType === TransactionType.EFT_WITHDRAWAL) {
      transactionRequest = fetchEftWithdrawTransaction({
        TransactionID: transactionID,
        Timezone: userSettings.timezone,
      });
    } else if (transactionHeader?.TransactionType === TransactionType.ACH_WITHDRAWAL) {
      transactionRequest = fetchAchWithdrawTransaction({
        TransactionID: transactionID,
        Timezone: userSettings.timezone,
      });
    }

    Promise.all([reportRequest, transactionRequest])
      .catch((error) =>
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred')),
      )
      .finally(() => blockingLoader.hide());
  }, [transactionID]);

  return (
    <div>
      {transaction && transactionHeader && (
        <AutoBalanceTransferTransactionInfo
          transaction={transaction}
          autoBalanceReportTransactions={autoBalanceReportTransactions}
          transactionHeader={transactionHeader}
          jwt={jwt}
          {...props}
        />
      )}
    </div>
  );
}
